/**
 * colors.ts - THE SINGLE SOURCE OF TRUTH FOR COLORS
 *
 * To add a new color:
 * 1. Add it to `palette` (raw color value) or `semantic` (references palette)
 * 2. Run `yarn generate:css-properties` to update custom-properties.css
 * That's it! Types are automatic.
 *
 * NOTE: The order of properties determine their order in the generated CSS.
 */

// Note: Moving away from using TagLt colors for tags

// PALETTE - Raw color values
export const palette = {
    black: '#000000',
    greyDk: '#211712',
    grey: '#4d4540',
    modalOverlay: 'hsla(234, 18%, 25%, 0.67)',
    greyLt: '#7a7370',
    placeholder: '#a6a3a1',
    sidebarHovered: '#cfcfd6',
    greyXlt: '#d4d1cf',
    buttonSecondaryDk: '#dbd9d9',
    offWhiteMd: '#ebe8e8',
    skeletonGrey: '#f1f1f1',
    offWhite: '#f7f7f7',
    offWhiteTransparent: 'rgba(247, 247, 247, 0)',
    white: '#ffffff',

    dotcomPurple: '#660347',
    plum: '#3d3b8a',
    aiPurpleDk: '#4d22a3',
    aiPurplePrimary: '#602bcc',
    company: '#514fb8',
    aiPurpleMd: '#7744e1',
    aiPurpleLt: '#8b5cf6',
    aiPurpleXlt: '#a378f6',
    aiPurplePink: '#bb97f6',
    aiPurplePinkMd: '#e2a9ff',
    tagPurpleDk: '#7373c7',
    plumBgDk: '#acace1',
    tagPurple: '#c8c8e9',
    aiPurplePinkLt: '#e6ddf7',
    plumLt: '#dbdbf2',
    tagPurpleLt: '#ececf8',

    plumXlt: '#e9e9f6',
    aiPurplePinkXlt: '#f2eefb',
    aiPurplePinkXxlt: '#fbfaff',

    aiBlue: '#0c0f32',
    aiHover: '#0a0d2b',
    sidebarPrimary: '#0c0f33',
    rcfxSidebar: '#1c2c48',
    rcfxSidebarHover: '#1c293f',
    rcfxSidebarPrimaryItem: '#142034',
    sidebarListLoading: '#333557',
    supportHover: '#292967',
    sidebarSecondary: '#242854',
    quoteToolbarBackground: '#222f3e',
    quoteToolbarButtonBackground: '#394452',
    reportNavy: '#3d405c',
    navyBgNew: '#333766',
    secondarySidebarHoverBlue: '#3f4372',
    navyDkNew: '#404372',
    navyBgActiveNew: '#5c5f87',
    navyLtNew: '#5977a4',
    secondarySidebarText: '#9397ba',
    focusBlue: '#065289',
    rcfxSidebarActive: '#0c57ad',
    blueDk: '#337da8',
    nutshellPro: '#4f86c6',
    blue: '#3b9ed7',
    dotcomTeal: '#62d4ca',
    reportBlue: '#61b2de',
    navyXltNew: '#94d1fe',
    tagBlue: '#c0e0f2',
    blueLt: '#d9edf7',
    listSelected: '#e8f4fb',
    blueXlt: '#e8f4fa',
    blueBgLt: '#eff5f7',
    navyBg: '#f7fafc',
    infoBg: '#eaf5fb',
    // blueLt, but for a fade in dynamic-cards.css
    tinyIntroTealTransparent: 'rgba(217, 237, 247, 0)',

    green50: '#f7fcf7',
    green100: '#ecf9ef',
    green200: '#d6f2de',
    green300: '#c4eecc',
    green400: '#5ccf73',
    green500: '#33c252',
    green600: '#2e9942',
    green700: '#0b8d6c',
    green800: '#165024',
    green900: '#113c1b',

    yellow50: '#fffcf7',
    yellow100: '#fff8e5',
    yellow150: '#fff5d9',
    yellow200: '#fff0c5',
    yellow300: '#fde9b4',
    yellow400: '#ffde73',
    yellow500: '#fac947',
    yellow600: '#e1b233',
    yellow700: '#bd9633',
    yellow800: '#a37d23',
    yellow900: '#7e6019',

    orangeDk: '#bf4200',
    orangeAttention: '#ea7134',
    orange: '#fe5800',
    tagOrangeDk: '#ff7833',
    quoteSignatureOrange: '#e39138',
    tagOrange: '#ffccb2',
    orangeLt: '#ffdecc',
    tagOrangeLt: '#ffeee5',
    orangeXlt: '#faf0e6',
    orangeBg: '#fff0e9',
    reportOrangeBg: '#fff7f5',

    roseDk: '#bf2436',
    flashBannerErrorText: 'hsl(353, 68%, 45%)',
    rose: '#ff2e47',
    reportRed: '#ff596b',
    tagRed: '#ffb2bb',
    flashBannerErrorBackground: 'hsl(356, 100%, 92%)',
    roseLt: '#ffd6d9',
    tagRedLt: '#ffe5e7',
    roseXlt: '#fff7f7',

    // Social platform/integrations colors
    messengerBlue: '#3b5998',
    instagramPink: '#dd2a7b',
    whatsappGreen: '#25d366',
    whatsappGreenDk: '#075e54',
    zoomBlue: '#0b5cff',
    googleGreen: '#05ac47',
    teamsBlue: '#464eb8',
    socialTwitter: '#00aced',
    socialLinkedin: '#007bb6',
    socialFoursquare: '#ef4b78',
    socialPinterest: '#cb2027',
    socialYoutube: '#e52d27',
    socialFlickr: '#ff0084',
    socialVimeo: '#aad450',
    socialGooglePlus: '#dd4b39',
    socialTumblr: '#35465c',
    socialDribbble: '#ea4c89',
    socialBehance: '#1769ff',
    socialDelicious: '#3399ff',
    socialHackerNews: '#ff6600',
    socialInstagram: '#3f729b',
    socialLastfm: '#c3000d',
    socialReddit: '#ff4500',
    socialTwitch: '#6441a5',
    socialVine: '#00b488',
} as const;

// SEMANTIC - Colors that reference the palette
export const semantic = {
    // Core colors
    offWhiteDk: 'offWhiteMd',
    plumDk: 'plum',
    plumBg: 'plumXlt',
    red: 'rose',
    redLt: 'roseLt',
    brownLt: 'yellow150',
    brown: 'yellow200',
    brownDk: 'yellow700',
    yellowBg: 'yellow50',
    yellowLt: 'yellow150',
    yellow: 'yellow500',
    greenBg: 'green50',
    greenXlt: 'green100',
    greenLt: 'green200',
    green: 'green500',
    greenDk: 'green600',
    navyLt: 'blueLt', // Navy lt is deprecated
    blueBg: 'blueLt',
    navy: 'blueDk',
    navyDk: 'blueDk',

    // Status colors
    warningBg: 'yellow100',
    warning: 'orangeDk',
    upgrade: 'sidebarPrimary',
    alertBg: 'roseXlt',
    alert: 'roseDk',
    error: 'rose',
    overdue: 'roseDk',
    info: 'blueDk',
    support: 'plum',
    successBg: 'green100',
    success: 'green700',
    label: 'grey',

    // Text colors
    textHot: 'orange',
    textAccounts: 'plum',
    textContacts: 'blueDk',
    textUsers: 'orangeDk',

    // Record colors
    activity: 'roseDk',
    lead: 'green600',
    leadHovered: 'green700',
    person: 'blueDk',
    task: 'yellow700',
    product: 'yellow700',
    productBg: 'yellow700',
    productBgHovered: 'yellow500',

    // Icon colors
    iconPrimaryHovered: 'greyDk',
    iconPrimary: 'grey',
    iconSecondaryDk: 'greyLt',
    iconSecondary: 'greyXlt',
    iconPrimaryAction: 'greyXlt',
    iconPrimaryActionHovered: 'reportBlue',

    listHovered: 'blueLt',
    backgroundHot: 'orange',
    bannerBorderOrange: 'orangeAttention',
    formBorder: 'greyXlt',
    buttonSecondary: 'offWhiteMd',
    zebraStripe: 'offWhite',
    wallboardRed: 'roseXlt',
    sidebarRed: 'sidebarPrimary',

    // Tag colors
    tagGrey: 'greyXlt',
    tagGreyLt: 'offWhiteMd',
    tagGreenLt: 'green100',
    tagBlueLt: 'infoBg',
    tagBlueDk: 'blue',
    tagGreyDk: 'greyLt',
    tagRedDk: 'rose',
    tagYellowLt: 'yellow100',
    tagYellow: 'yellow300',
    tagYellowDk: 'yellow500',
    tagGreen: 'green300',
    tagGreenDk: 'green400',

    // Tiny intro colors
    tinyIntroLogin: 'blueLt',
    tinyIntroSignup: 'blueLt',
    tinyIntroTeal: 'blueLt',
    tinyIntroOffWhite: 'blueLt',

    // Onboarding colors
    onboardingHelp: 'company',
    onboardingHelpLt: 'tagPurpleDk',
    onboardingHelpDk: 'plum',

    // Report colors
    reportGreen: 'green400',
    reportOrange: 'tagOrangeDk',

    // Social platform colors
    socialFacebook: 'messengerBlue',
    socialX: 'black',
    socialAngelList: 'black',
    socialDigg: 'black',
    socialGithub: 'black',

    // AI Purple
    aiPurpleBg: 'aiPurplePinkXxlt',
    aiPurpleBorder: 'aiPurpleXlt',
    aiPurpleBoxShadow: 'aiPurplePinkLt',

    // AI Purple gradients - basic
    aiPurpleGradientBasicOne: 'aiPurpleLt',
    aiPurpleGradientBasicTwo: 'aiPurplePrimary',

    // AI Purple gradients - regular
    aiPurpleGradientOne: 'aiPurplePink',
    aiPurpleGradientTwo: 'aiPurpleLt',
    aiPurpleGradientThree: 'aiPurplePrimary',

    // AI Purple gradients - expanded
    aiPurpleGradientExpandedOne: 'aiPurplePinkMd',
    aiPurpleGradientExpandedTwo: 'aiPurplePink',
    aiPurpleGradientExpandedThree: 'aiPurpleLt',
    aiPurpleGradientExpandedFour: 'aiPurplePrimary',

    // AI Purple gradients - bold
    aiPurpleGradientBoldOne: 'aiPurplePinkMd',
    aiPurpleGradientBoldTwo: 'aiPurpleMd',
    aiPurpleGradientBoldThree: 'aiPurpleLt',
    aiPurpleGradientBoldFour: 'aiPurplePinkMd',
    aiPurpleGradientBoldFive: 'aiPurpleLt',
    aiPurpleGradientBoldSix: 'aiPurpleMd',
} as const;

function isPaletteKey(value: string): value is PaletteKey {
    return value in palette;
}

const resolvedSemantic: {[K in SemanticKey]: string} = {} as {
    [K in SemanticKey]: string;
};

for (const [key, value] of Object.entries(semantic)) {
    (resolvedSemantic as Record<string, string>)[key] = isPaletteKey(value)
        ? palette[value]
        : value;
}

export type PaletteKey = keyof typeof palette;
export type SemanticKey = keyof typeof semantic;
export type ColorName = keyof typeof colors;

export const colors = {
    ...palette,
    ...resolvedSemantic,
} as const;

export const colorDefinitions = {
    palette,
    semantic,
};
